import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirestoreDb } from '@/lib/firebase';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { SettingsSection } from '@/components/settings/SettingsSection';

export function CostCentersSection() {
  const { t } = useTranslation();
  const { orgId, online, canPost } = useSettingsAccess();
  const toast = useToast();
  const [ccName, setCcName] = useState('');
  const [adding, setAdding] = useState(false);

  const mapCostCenter = useCallback(
    (id: string, data: Record<string, unknown>) => ({ id, name: String(data.name ?? '') }),
    []
  );

  const costCentersPage = useFirestorePageQuery({
    orgId,
    collectionPath: 'costCenters',
    orderByField: 'name',
    orderDirection: 'asc',
    mapDoc: mapCostCenter,
    enabled: Boolean(orgId)
  });

  async function addCostCenter() {
    if (!orgId || !online || !canPost || !ccName.trim()) return;
    const trimmed = ccName.trim();
    setAdding(true);
    try {
      const dup = await getDocs(
        query(
          collection(getFirestoreDb(), 'organizations', orgId, 'costCenters'),
          where('name', '==', trimmed)
        )
      );
      if (!dup.empty) {
        toast.error(t('settings.costCenterDuplicate'), 'settings-cost-center');
        return;
      }
      await addDoc(collection(getFirestoreDb(), 'organizations', orgId, 'costCenters'), {
        name: trimmed,
        active: true,
        createdAt: serverTimestamp()
      });
      setCcName('');
      costCentersPage.refresh();
      toast.success(t('settings.costCenterAdded'), 'settings-cost-center');
    } catch (e) {
      toastError(toast, e, 'settings-cost-center');
    } finally {
      setAdding(false);
    }
  }

  return (
    <SettingsSection id="settings-cost-centers" lazy title={t('settings.costCenters')}>
      {(visible) =>
        visible ? (
          <>
            {canPost && (
              <div className="flex flex-wrap gap-2">
                <Input
                  className="min-w-[12rem] flex-1"
                  value={ccName}
                  onChange={(e) => setCcName(e.target.value)}
                  placeholder={t('settings.costCenterName')}
                  disabled={!online}
                />
                <Button
                  variant="secondary"
                  disabled={!online || !orgId || !ccName.trim() || adding}
                  onClick={() => void addCostCenter()}
                >
                  {t('settings.addCostCenter')}
                </Button>
              </div>
            )}
            <PaginatedTable
              loading={costCentersPage.loading}
              error={costCentersPage.error}
              pageIndex={costCentersPage.pageIndex}
              hasNext={costCentersPage.hasNext}
              hasPrev={costCentersPage.hasPrev}
              onNext={costCentersPage.nextPage}
              onPrev={costCentersPage.prevPage}
              pageSize={costCentersPage.pageSize}
              onPageSizeChange={costCentersPage.setPageSize}
            >
              <ul className="divide-y divide-[var(--fv-border)] text-sm">
                {costCentersPage.rows.map((c) => (
                  <li key={c.id} className="px-3 py-2 transition-colors hover:bg-[var(--fv-bg)]">
                    {c.name}
                  </li>
                ))}
              </ul>
            </PaginatedTable>
          </>
        ) : null
      }
    </SettingsSection>
  );
}
